/**
 * Evita mistura de folha entre empresas (ex.: códigos curtos de uma empresa
 * com CNPJ/schema de outra). Códigos eSocial "longos" costumam embutir o CNPJ.
 */

export function normalizeCnpjDigits(raw) {
  return String(raw || "").replace(/\D/g, "");
}

export function getCodigoRubricaFromItem(item) {
  if (!item || typeof item !== "object") return "";
  return (
    item.codRubrica ??
    item.codigoRubrica ??
    item.codrubr ??
    item.codigo_rubrica ??
    ""
  );
}

function digitLenOfCodigo(item) {
  return String(getCodigoRubricaFromItem(item)).replace(/\D/g, "").length;
}

function codigoEmbedsCnpj(item, cnpj14) {
  const d = String(getCodigoRubricaFromItem(item)).replace(/\D/g, "");
  return d.includes(cnpj14);
}

/** Códigos da folha com muitos dígitos tendem a ser o padrão com CNPJ embutido */
const LONG_CODE_MIN_DIGITS = 16;

/**
 * Remove linhas longas que não embutem o CNPJ da empresa e, quando há linhas longas
 * válidas para o CNPJ, remove códigos curtos (mistura típica com outra empresa).
 */
export function filterFolhaItemsByCnpjCoherence(items, cnpjRaw) {
  if (!Array.isArray(items) || items.length === 0) return items;
  const cnpj = normalizeCnpjDigits(cnpjRaw);
  if (cnpj.length !== 14) return items;

  let hasOurLong = false;
  let hasAlienLong = false;
  for (const it of items) {
    const len = digitLenOfCodigo(it);
    if (len >= LONG_CODE_MIN_DIGITS) {
      if (codigoEmbedsCnpj(it, cnpj)) hasOurLong = true;
      else hasAlienLong = true;
    }
  }

  if (!hasOurLong && !hasAlienLong) return items;

  return items.filter((it) => {
    const len = digitLenOfCodigo(it);
    if (len >= LONG_CODE_MIN_DIGITS) return codigoEmbedsCnpj(it, cnpj);
    return !hasOurLong;
  });
}

/**
 * Quando a folha ao vivo tem códigos longos com o CNPJ, mas o snapshot só tem
 * códigos curtos, o snapshot quase sempre é lixo cruzado — manter a folha ao vivo.
 */
export function preferLiveFolhaOverSnapshot(liveItems, snapshotItems, cnpjRaw) {
  const cnpj = normalizeCnpjDigits(cnpjRaw);
  if (cnpj.length !== 14) return false;
  if (!Array.isArray(liveItems) || liveItems.length === 0) return false;
  if (!Array.isArray(snapshotItems) || snapshotItems.length === 0) return false;

  const liveHasOurLong = liveItems.some(
    (it) =>
      digitLenOfCodigo(it) >= LONG_CODE_MIN_DIGITS && codigoEmbedsCnpj(it, cnpj)
  );
  if (!liveHasOurLong) return false;

  const snapHasOurLong = snapshotItems.some(
    (it) =>
      digitLenOfCodigo(it) >= LONG_CODE_MIN_DIGITS && codigoEmbedsCnpj(it, cnpj)
  );
  const snapOnlyShort = snapshotItems.every(
    (it) => digitLenOfCodigo(it) < LONG_CODE_MIN_DIGITS
  );

  return !snapHasOurLong && snapOnlyShort;
}

function getCodigoFromResumoItem(item) {
  if (!item || typeof item !== "object") return "";
  return (
    item.codigoRubricaCompleto ??
    item.codigoRubrica ??
    item.codRubrica ??
    ""
  );
}

export function filterResumoItemsByCnpjCoherence(items, cnpjRaw) {
  if (!Array.isArray(items) || items.length === 0) return items;
  const cnpj = normalizeCnpjDigits(cnpjRaw);
  if (cnpj.length !== 14) return items;

  const mapped = items.map((row) => ({
    ...row,
    __cod: getCodigoFromResumoItem(row),
  }));

  let hasOurLong = false;
  let hasAlienLong = false;
  for (const row of mapped) {
    const len = String(row.__cod).replace(/\D/g, "").length;
    if (len >= LONG_CODE_MIN_DIGITS) {
      if (String(row.__cod).replace(/\D/g, "").includes(cnpj)) hasOurLong = true;
      else hasAlienLong = true;
    }
  }

  if (!hasOurLong && !hasAlienLong) {
    return items;
  }

  return mapped
    .filter((row) => {
      const digits = String(row.__cod).replace(/\D/g, "");
      const len = digits.length;
      if (len >= LONG_CODE_MIN_DIGITS) return digits.includes(cnpj);
      return !hasOurLong;
    })
    .map(({ __cod, ...rest }) => rest);
}

export function sanitizeVersaoSnapshotArrays(versaoObj, cnpjRaw) {
  if (!versaoObj || typeof versaoObj !== "object") return versaoObj;
  const cnpj = normalizeCnpjDigits(cnpjRaw);
  if (cnpj.length !== 14) return versaoObj;

  const next = { ...versaoObj };
  if (Array.isArray(next.verbasComValores)) {
    next.verbasComValores = filterFolhaItemsByCnpjCoherence(
      next.verbasComValores,
      cnpj
    );
  }
  if (Array.isArray(next.resumo)) {
    next.resumo = filterResumoItemsByCnpjCoherence(next.resumo, cnpj);
  }
  if (Array.isArray(next.resumoAtual)) {
    next.resumoAtual = filterResumoItemsByCnpjCoherence(next.resumoAtual, cnpj);
  }
  if (Array.isArray(next.verbasAjustadas)) {
    next.verbasAjustadas = filterFolhaItemsByCnpjCoherence(
      next.verbasAjustadas,
      cnpj
    );
  }
  return next;
}
